import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { AMAZON_MONTH_END_CLOSE_SUMMARY } from '../../consts/reports';
import { AMAZON_MONTH_END_CLOSE_SUMMARY_TYPE } from '../../consts/cronTypes';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split('T')[0];
})();

const AmazonMonthEndCloseSummaryReport = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['amazon-month-end-close-summary'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['amazon-month-end-close-summary'] || [];
  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);
  const [refreshStatus, setRefreshStatus] = useState('ready'); // 'loading' | 'errorred' | 'ready'
  const [refreshError, setRefreshError] = useState(null);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const pollingRef = useRef(null);
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(async () => {
      try {
        const response = await fetch('/api/v1/reports/amazon-month-end-close-summary/refresh-fba/status', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch refresh status');
        }
        const data = await response.json();
        const statusData = data.success ? data : data.data || data;
        
        const newStatus = statusData.status || 'ready';
        
        // Update state
        setRefreshStatus(newStatus);
        setRefreshError(statusData.process_error || null);
        
        // If status changed from loading to ready or errorred, stop polling and refresh report data
        if (newStatus !== 'loading') {
          stopPolling();
          // Refresh report data when job completes
          if (newStatus === 'ready') {
            await fetchReportData();
          }
        }
      } catch (err) {
        console.error('Error polling refresh status:', err);
        // Don't stop polling on error, just log it
      }
    }, 4000);
  };

  const checkRefreshStatus = async () => {
    try {
      const response = await fetch('/api/v1/reports/amazon-month-end-close-summary/refresh-fba/status', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch refresh status');
      }
      const data = await response.json();
      const statusData = data.success ? data : data.data || data;
      
      setRefreshStatus(statusData.status || 'ready');
      setRefreshError(statusData.process_error || null);
      
      // If status is loading, start polling
      if (statusData.status === 'loading') {
        startPolling();
      }
    } catch (err) {
      console.error('Error checking refresh status:', err);
      // Don't set error state here, just default to ready
      setRefreshStatus('ready');
    }
  };

  useEffect(() => {
    fetchReportMetadata();
    checkRefreshStatus();
    dispatch(getReportEmailSettings('amazon-month-end-close-summary'));
    dispatch(getReportSendHistory('amazon-month-end-close-summary'));
  }, [dispatch]);

  useEffect(() => {
    if (!initialReady) return;
    if (startDate && endDate) {
      fetchReportData();
    }
  }, [initialReady, startDate, endDate]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${AMAZON_MONTH_END_CLOSE_SUMMARY}/metadata`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch report metadata');
      const data = await response.json();
      setColumns(data.columns || []);
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const fetchReportData = async () => {
    if (!startDate || !endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await fetch(`/api/v1/reports/${AMAZON_MONTH_END_CLOSE_SUMMARY}?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch report data');
      }
      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  };

  const handleDateChange = ({ startDate: s, endDate: e }) => {
    setDateRange({ startDate: s, endDate: e });
  };

  const handleBack = () => {
    navigate(ROUTES.REPORTS);
  };

  const handleExport = async () => {
    if (!startDate || !endDate) return;
    const params = new URLSearchParams({ startDate, endDate });
    const response = await fetch(`/api/v1/reports/${AMAZON_MONTH_END_CLOSE_SUMMARY}/export?${params}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `amazon-month-end-close-summary_${new Date().toISOString().slice(0,10)}.csv`;
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  useEffect(() => {
    return () => stopPolling();
  }, []);

  const handleRefreshFbaData = async () => {
    if (!startDate || !endDate || refreshStatus === 'loading') return;
    
    setRefreshStatus('loading');
    setRefreshError(null);
    setError(null);
    
    try {
      const response = await fetch('/api/v1/reports/amazon-month-end-close-summary/refresh-fba', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ startDate, endDate })
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to start refresh');
      }
      
      // Start polling for status updates
      startPolling();
    } catch (err) {
      console.error('Refresh FBA data failed:', err);
      setRefreshError(err.message);
      setRefreshStatus('errorred');
      stopPolling();
    }
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' }
  ];

  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={error && error.includes('date') ? error : null}
    />
  );

  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${AMAZON_MONTH_END_CLOSE_SUMMARY}`}
      onExport={handleExport}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  const refreshButton = (
    <div className="flex flex-col items-start">
      <button
        onClick={handleRefreshFbaData}
        disabled={refreshStatus === 'loading' || !startDate || !endDate}
        className={
          refreshStatus === 'loading' || !startDate || !endDate
            ? 'inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
            : 'inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
        }
      >
        {refreshStatus === 'loading' 
          ? 'Refreshing FBA Data…' 
          : refreshStatus === 'errorred'
          ? 'Refresh FBA Data (Last run failed)'
          : 'Refresh FBA Data'}
      </button>
      {refreshStatus === 'errorred' && refreshError && (
        <p className="mt-1 text-sm text-red-600">{refreshError}</p>
      )}
    </div>
  );

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'amazon-month-end-close-summary', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || AMAZON_MONTH_END_CLOSE_SUMMARY_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'amazon-month-end-close-summary', ...payload })).unwrap();
      await dispatch(getReportSendHistory('amazon-month-end-close-summary')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'amazon-month-end-close-summary', ...payload })).unwrap();
      await dispatch(getReportSendHistory('amazon-month-end-close-summary')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const renderRows = () => {
    return reportData.map((row, idx) => (
      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
        {columns.map(col => (
          <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
            {row[col.key]}
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <>
      <ReportTemplate
        reportId={AMAZON_MONTH_END_CLOSE_SUMMARY}
        description="Summarized Amazon FBA financial metrics for the selected period"
        breadcrumbs={breadcrumbs}
        isFirstColumnSticky={false}
        marginFromSidebar={marginFromSidebar}
        onBack={handleBack}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        cronType={emailSettings?.cronType || null}
        exportButton={
          <div className="flex items-center space-x-2">
            {exportButton}
            <div>
              <Cog8ToothIcon
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  setIsOpenSettings(true);
                }}
              />
            </div>
            {refreshButton}
          </div>
        }
        data={reportData}
        columns={columns}
        enableSorting={false}
      >
        {renderRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'amazon-month-end-close-summary'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="amazon-month-end-close-summary"
      />
    </>
  );
};

export default AmazonMonthEndCloseSummaryReport;
